/**
 * TechnicianMaterials Page - Material Requests Management
 *
 * Materials management for technicians.
 * Shows material request history with filters and statistics.
 *
 * @module pages/technician/TechnicianMaterials
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Container, Button, Spinner, Alert, Row, Col, InputGroup, Form, Collapse, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { getMaterialRequests } from '../../services/materialRequestService';
import { getAssignedProjects } from '../../services/technicianProjectService';
import { unwrapServiceResult } from '../../utils/serviceResult';
import { MaterialRequestCard, QuickRequestShortcut } from './materials';
import {
  MATERIAL_REQUEST_STATUS,
  MATERIAL_REQUEST_STATUS_LABELS,
} from '../../utils/constants';
import useViewToggle from '../../hooks/useViewToggle';
import ViewToggle from '../../components/common/ViewToggle';
import './TechnicianPages.css';
import './materials/MaterialRequestForm.css';

const TechnicianMaterials = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { viewMode, setViewMode } = useViewToggle();
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Fetch material requests
  const {
    data: requestsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['material-requests', user?.id, statusFilter],
    // `getMaterialRequests` cierra con `catch { return { data:null, count:0,
    // error } }`: RESUELVE. Sin el unwrap el `error` de react-query es
    // estructuralmente siempre null, el `{error && <Alert>}` de abajo es
    // inalcanzable, la lista cae a "No tienes solicitudes" y los cuatro
    // contadores dicen 0 — que el técnico lee como "mi pedido no entró".
    // Ver `src/utils/serviceResult.js`.
    queryFn: () =>
      unwrapServiceResult(
        getMaterialRequests(user?.id, {
          status: statusFilter !== 'all' ? statusFilter : undefined,
          pageSize: 50,
        })
      ),
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: true,
  });

  // Fetch assigned projects for client/project filter
  const { data: projectsResponse } = useQuery({
    queryKey: ['technician-projects', user?.id],
    queryFn: () => getAssignedProjects(user?.id, { pageSize: 50 }),
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5,
  });

  const allProjects = useMemo(() => {
    return projectsResponse?.data || [];
  }, [projectsResponse?.data]);

  // Extract unique clients from projects
  const uniqueClients = useMemo(() => {
    const clientMap = new Map();
    allProjects.forEach((p) => {
      if (p.client?.id) {
        clientMap.set(p.client.id, p.client.company_name || p.client.primary_contact_name || 'Sin nombre');
      }
    });
    return Array.from(clientMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allProjects]);

  // Projects filtered by client (cascade)
  const filteredProjects = useMemo(() => {
    if (!clientFilter) return allProjects;
    return allProjects.filter((p) => p.client?.id === clientFilter);
  }, [allProjects, clientFilter]);

  // Set of project IDs that match the current client/project filters
  const allowedProjectIds = useMemo(() => {
    if (projectFilter) return new Set([projectFilter]);
    if (clientFilter) return new Set(filteredProjects.map((p) => p.id));
    return null; // null means no filtering
  }, [projectFilter, clientFilter, filteredProjects]);

  const requests = useMemo(() => requestsData?.data || [], [requestsData?.data]);

  // Calculate statistics
  const stats = useMemo(() => {
    const allRequests = requests;
    return {
      total: allRequests.length,
      pending: allRequests.filter(
        (r) => r.status === MATERIAL_REQUEST_STATUS.PENDING
      ).length,
      approved: allRequests.filter(
        (r) => r.status === MATERIAL_REQUEST_STATUS.APPROVED
      ).length,
      delivered: allRequests.filter(
        (r) => r.status === 'delivered'
      ).length,
      rejected: allRequests.filter(
        (r) => r.status === MATERIAL_REQUEST_STATUS.REJECTED
      ).length,
    };
  }, [requests]);

  // Handle request cancellation
  const handleRequestCancelled = useCallback(() => {
    queryClient.invalidateQueries(['material-requests']);
    refetch();
  }, [queryClient, refetch]);

  // Filter options
  const filterOptions = [
    { value: 'all', label: 'Todas', count: stats.total },
    {
      value: MATERIAL_REQUEST_STATUS.PENDING,
      label: 'Pendientes',
      count: stats.pending,
    },
    {
      value: MATERIAL_REQUEST_STATUS.APPROVED,
      label: 'Aprobadas',
      count: stats.approved,
    },
    {
      value: 'delivered',
      label: 'Entregadas',
      count: stats.delivered,
    },
    {
      value: MATERIAL_REQUEST_STATUS.REJECTED,
      label: 'Rechazadas',
      count: stats.rejected,
    },
  ];

  // Filter requests by search term and client/project
  const filteredRequests = useMemo(() => {
    let result = requests;

    // Filter by client/project
    if (allowedProjectIds) {
      result = result.filter((r) => r.project?.id && allowedProjectIds.has(r.project.id));
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      result = result.filter(r =>
        r.inventory_item?.name?.toLowerCase().includes(search) ||
        r.notes?.toLowerCase().includes(search) ||
        r.external_description?.toLowerCase().includes(search)
      );
    }

    return result;
  }, [requests, searchTerm, allowedProjectIds]);

  return (
    <Container fluid className="technician-page material-request-page">
      {/* Page Header */}
      <div className="material-request-header">
        <h1 className="page-title">Materiales</h1>
        <p className="page-subtitle">Gestiona tus solicitudes de materiales</p>
      </div>

      {/* Action Button — sigue siendo el camino primario: full-width, color
          primario y primero en la pagina. El atajo de abajo no lo reemplaza. */}
      <div className="d-grid mb-3">
        <Button
          as={Link}
          to="/tecnico/materiales/solicitar"
          variant="primary"
          size="lg"
          className="action-btn"
        >
          <i className="bi bi-plus-circle me-2"></i>
          Solicitar Material
        </Button>
      </div>

      {/* Camino rapido — dictar o sacar una foto en vez de llenar campos.
          Vivia solo en /tecnico/materiales/smart, sin un solo enlace desde
          aca. El porque de esta forma esta en QuickRequestShortcut.js. */}
      <QuickRequestShortcut />

      {/* Statistics Cards
          Con la lectura caída ninguno de los cuatro puede decir 0: "no tenés
          solicitudes pendientes" es un hecho de negocio, y afirmarlo por un
          corte de red es lo que hace que un pedido de material quede invisible.
          Un guion no afirma nada; el Alert de arriba explica por qué. */}
      <Row className="g-2 mb-4">
        <Col xs={6} sm={3}>
          <div className="stat-card dashboard-card" style={{ cursor: 'pointer' }} onClick={() => setStatusFilter('pending')} data-testid="material-stat-pending">
            <div className="card-body text-center">
              <i className="bi bi-hourglass-split text-warning"></i>
              <div className="stat-number">{isError ? '—' : stats.pending}</div>
              <div className="stat-label">Pendientes</div>
            </div>
          </div>
        </Col>
        <Col xs={6} sm={3}>
          <div className="stat-card dashboard-card" style={{ cursor: 'pointer' }} onClick={() => setStatusFilter('approved')} data-testid="material-stat-approved">
            <div className="card-body text-center">
              <i className="bi bi-check-circle text-success"></i>
              <div className="stat-number">{isError ? '—' : stats.approved}</div>
              <div className="stat-label">Aprobadas</div>
            </div>
          </div>
        </Col>
        <Col xs={6} sm={3}>
          <div className="stat-card dashboard-card" style={{ cursor: 'pointer' }} onClick={() => setStatusFilter('delivered')} data-testid="material-stat-delivered">
            <div className="card-body text-center">
              <i className="bi bi-truck text-primary"></i>
              <div className="stat-number">{isError ? '—' : stats.delivered}</div>
              <div className="stat-label">Entregadas</div>
            </div>
          </div>
        </Col>
        <Col xs={6} sm={3}>
          <div className="stat-card dashboard-card" style={{ cursor: 'pointer' }} onClick={() => setStatusFilter('rejected')} data-testid="material-stat-rejected">
            <div className="card-body text-center">
              <i className="bi bi-x-circle text-danger"></i>
              <div className="stat-number">{isError ? '—' : stats.rejected}</div>
              <div className="stat-label">Rechazadas</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Search Bar + Advanced Filters */}
      <div className="mb-3">
        <InputGroup>
          <InputGroup.Text style={{ background: '#f8f9fa', border: '1px solid #dee2e6', color: '#6c757d' }}>
            <i className="bi bi-search"></i>
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Buscar solicitudes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Buscar solicitudes de materiales"
          />
          <Button
            variant={statusFilter !== 'all' || clientFilter || projectFilter ? 'primary' : 'outline-secondary'}
            className="d-flex align-items-center gap-1"
            onClick={() => setShowAdvanced(!showAdvanced)}
            aria-expanded={showAdvanced}
          >
            <i className={`bi bi-chevron-${showAdvanced ? 'up' : 'down'}`}></i>
            <span className="d-none d-sm-inline">Filtros</span>
            {(statusFilter !== 'all' || clientFilter || projectFilter) && (
              <Badge bg="light" text="dark" pill style={{ fontSize: '0.65rem' }}>
                {(statusFilter !== 'all' ? 1 : 0) + (clientFilter ? 1 : 0) + (projectFilter ? 1 : 0)}
              </Badge>
            )}
          </Button>
          <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
        </InputGroup>

        <Collapse in={showAdvanced}>
          <div>
            <div className="d-flex gap-2 mt-2 flex-wrap align-items-center">
              <Form.Select
                size="sm"
                value={clientFilter}
                onChange={(e) => {
                  const val = e.target.value;
                  setClientFilter(val);
                  // Clear project filter if it no longer belongs to the selected client
                  if (val && projectFilter) {
                    const valid = allProjects.some(
                      (p) => p.id === projectFilter && p.client?.id === val
                    );
                    if (!valid) setProjectFilter('');
                  }
                }}
                style={{ maxWidth: '200px' }}
                aria-label="Filtrar por cliente"
              >
                <option value="">Todos los clientes</option>
                {uniqueClients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Form.Select>
              <Form.Select
                size="sm"
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                style={{ maxWidth: '200px' }}
                aria-label="Filtrar por proyecto"
              >
                <option value="">Todos los proyectos</option>
                {filteredProjects.map((p) => (
                  <option key={p.id} value={p.id}>{p.title || p.project_number}</option>
                ))}
              </Form.Select>
            </div>
            <div className="d-flex gap-2 mt-2 flex-wrap align-items-center">
              {filterOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`filter-tab ${statusFilter === option.value ? 'active' : ''}`}
                  onClick={() => setStatusFilter(option.value)}
                >
                  {option.label}
                  {option.count > 0 && (
                    <span className="filter-tab-count">{option.count}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </Collapse>
      </div>

      {/* Error Alert */}
      {isError && (
        <Alert variant="danger" className="mb-3">
          <i className="bi bi-exclamation-circle me-2"></i>
          Error al cargar las solicitudes. Por favor, intenta de nuevo.
          <div className="small text-muted mt-1">{error?.message}</div>
          <Button variant="outline-danger" size="sm" className="mt-2" onClick={() => refetch()}>
            <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
            Reintentar
          </Button>
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="loading-container">
          <Spinner animation="border" variant="primary" />
          <span className="loading-text">Cargando solicitudes...</span>
        </div>
      )}

      {/* Request List */}
      {!isLoading && !isError && (
        <>
          {filteredRequests.length === 0 ? (
            <div className="empty-state dashboard-card">
              <i className="bi bi-box-seam empty-state-icon"></i>
              <h4 className="empty-state-title">
                {statusFilter === 'all'
                  ? 'No tienes solicitudes'
                  : `No hay solicitudes ${MATERIAL_REQUEST_STATUS_LABELS[statusFilter]?.toLowerCase() || ''}`}
              </h4>
              <p className="empty-state-text">
                {statusFilter === 'all'
                  ? 'Solicita materiales para tus proyectos usando el boton de arriba.'
                  : 'Cambia el filtro para ver otras solicitudes.'}
              </p>
            </div>
          ) : (
            <div className="request-list">
              {filteredRequests.map((request) => (
                <MaterialRequestCard
                  key={request.id}
                  request={request}
                  onCancelled={handleRequestCancelled}
                />
              ))}
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default TechnicianMaterials;
